/**
 * Mobile Menu Handler - BRONCANO
 * Manages hamburger menu functionality for mobile devices
 */

class MobileMenu {
    constructor() {
        this.menuToggle = document.querySelector('.mobile-menu-toggle');
        this.mobileNav = document.querySelector('.mobile-nav');
        this.menuOverlay = document.querySelector('.mobile-nav-overlay');
        this.menuLinks = document.querySelectorAll('.mobile-nav-links a');
        this.body = document.body;
        
        this.isOpen = false;
        
        this.init();
    }
    
    init() {
        if (!this.menuToggle || !this.mobileNav) {
            console.warn('Mobile menu elements not found');
            return;
        }
        
        this.bindEvents();
        this.setupAccessibility();
    }
    
    bindEvents() {
        // Toggle menu
        this.menuToggle.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggleMenu();
        });
        
        // Close on overlay click (primary modern way to close)
        if (this.menuOverlay) {
            this.menuOverlay.addEventListener('click', () => {
                this.closeMenu();
            });
        }
        
        // Close on link click
        this.menuLinks.forEach(link => {
            link.addEventListener('click', () => {
                this.closeMenu();
            });
        });
        
        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closeMenu();
            }
        });
        
        // Handle window resize
        window.addEventListener('resize', () => {
            if (window.innerWidth > 768 && this.isOpen) {
                this.closeMenu();
            }
        });
        
        // Handle orientation change
        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                if (window.innerWidth > 768 && this.isOpen) {
                    this.closeMenu();
                }
            }, 100);
        });
    }
    
    setupAccessibility() {
        // Set initial ARIA states
        this.menuToggle.setAttribute('aria-expanded', 'false');
        this.mobileNav.setAttribute('aria-hidden', 'true');
        
        // Add role attributes
        this.mobileNav.setAttribute('role', 'navigation');
        this.mobileNav.setAttribute('aria-label', 'Mobile navigation');
        
        // Focus management
        this.menuLinks.forEach((link) => {
            link.setAttribute('tabindex', this.isOpen ? '0' : '-1');
        });
    }
    
    toggleMenu() {
        if (this.isOpen) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }
    
    openMenu() {
        this.isOpen = true;
        
        // Update classes
        this.mobileNav.classList.add('active');
        this.body.classList.add('mobile-menu-open');
        
        // Update ARIA states
        this.menuToggle.setAttribute('aria-expanded', 'true');
        this.mobileNav.setAttribute('aria-hidden', 'false');
        
        // Enable focus on menu items
        this.menuLinks.forEach(link => {
            link.setAttribute('tabindex', '0');
        });
        
        // Focus management - focus on first menu link
        setTimeout(() => {
            if (this.menuLinks[0]) {
                this.menuLinks[0].focus();
            }
        }, 300);
        
        // Prevent scrolling on iOS
        this.preventScroll();
        
        console.log('Mobile menu opened');
    }
    
    closeMenu() {
        this.isOpen = false;
        
        // Update classes
        this.mobileNav.classList.remove('active');
        this.body.classList.remove('mobile-menu-open');
        
        // Update ARIA states
        this.menuToggle.setAttribute('aria-expanded', 'false');
        this.mobileNav.setAttribute('aria-hidden', 'true');
        
        // Disable focus on menu items
        this.menuLinks.forEach(link => {
            link.setAttribute('tabindex', '-1');
        });
        
        // Return focus to toggle button
        this.menuToggle.focus();
        
        // Re-enable scrolling
        this.enableScroll();
        
        console.log('Mobile menu closed');
    }
    
    preventScroll() {
        // Store current scroll position
        this.scrollPosition = window.pageYOffset;
        
        // Apply styles to prevent scroll
        this.body.style.overflow = 'hidden';
        this.body.style.position = 'fixed';
        this.body.style.width = '100%';
        this.body.style.top = `-${this.scrollPosition}px`;
    }
    
    enableScroll() {
        // Remove scroll prevention styles
        this.body.style.overflow = '';
        this.body.style.position = '';
        this.body.style.width = '';
        this.body.style.top = '';
        
        // Restore scroll position
        if (this.scrollPosition) {
            window.scrollTo(0, this.scrollPosition);
        }
    }
    
    // Public method to close menu (for external use)
    close() {
        this.closeMenu();
    }
    
    // Public method to check if menu is open
    get isMenuOpen() {
        return this.isOpen;
    }
}

// Initialize mobile menu when DOM is ready
function initMobileMenu() {
    const mobileMenu = new MobileMenu();
    
    // Make available globally for debugging
    window.mobileMenu = mobileMenu;
    
    return mobileMenu;
}

// Auto-initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileMenu);
} else {
    initMobileMenu();
}

// Re-initialize on Turbo navigation
document.addEventListener('turbo:load', initMobileMenu);

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MobileMenu;
}